import { useState, useEffect, useCallback } from "react";
import * as taskService from "../services/taskService";

/**
 * Normaliza una tarea del API para que los componentes UI
 * puedan seguir usando task.category, task.priority, task.status como keys.
 */
const normalizeTask = (t) => ({
  ...t,
  id: t.taskId,
  category: t.categoryKey,
  priority: t.priorityKey,
  status: t.statusKey,
  dueDate: t.dueDate ? t.dueDate.split("T")[0] : null,
  createdBy: t.creatorName,
});

/**
 * Hook para gestión de tareas clínicas via API.
 *
 * @param {number|null} locationId - ID de la location actual (para filtrar tareas)
 * @returns {{ tasks, stats, loading, createTask, completeTask, updateTask, deleteTask, refreshTasks }}
 */
export default function useTasks(locationId) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({
    totalTasks: 0,
    totalPending: 0,
    totalInProgress: 0,
    totalCompleted: 0,
    totalCancelled: 0,
    totalHigh: 0,
    totalMedium: 0,
    totalLow: 0,
    totalOverdue: 0,
    totalActive: 0,
  });
  const [loading, setLoading] = useState(false);

  const refreshTasks = useCallback(async () => {
    setLoading(true);
    try {
      const filters = locationId ? { locationId } : {};
      const [tasksData, statsData] = await Promise.all([
        taskService.getTasks(filters),
        taskService.getTaskStats(filters),
      ]);
      setTasks(Array.isArray(tasksData) ? tasksData.map(normalizeTask) : []);
      setStats(statsData || {});
    } catch (err) {
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    refreshTasks();
  }, [refreshTasks]);

  const createTask = useCallback(
    async (taskData) => {
      try {
        const result = await taskService.createTask({
          ...taskData,
          locationId: taskData.locationId || locationId,
        });
        await refreshTasks();
        return result;
      } catch (err) {
        console.error("Error creating task:", err);
        throw err;
      }
    },
    [locationId, refreshTasks]
  );

  const completeTask = useCallback(
    async (taskId, employeeId) => {
      try {
        await taskService.completeTask(taskId, employeeId);
        await refreshTasks();
      } catch (err) {
        console.error("Error completing task:", err);
      }
    },
    [refreshTasks]
  );

  const updateTask = useCallback(
    async (taskId, newStatus, employeeId) => {
      try {
        await taskService.updateTaskStatus(taskId, newStatus, employeeId);
        await refreshTasks();
      } catch (err) {
        console.error("Error updating task:", err);
      }
    },
    [refreshTasks]
  );

  const deleteTask = useCallback(
    async (taskId, employeeId) => {
      try {
        await taskService.deleteTask(taskId, employeeId);
        await refreshTasks();
      } catch (err) {
        console.error("Error deleting task:", err);
      }
    },
    [refreshTasks]
  );

  return {
    tasks,
    stats,
    loading,
    createTask,
    completeTask,
    updateTask,
    deleteTask,
    refreshTasks,
  };
}
